import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";

export type AdminCronRisk = "dryRun" | "prod" | "verification";
export type AdminCronParamType = "text" | "email" | "number" | "checkbox";

export interface AdminCronParam {
  key: string;
  label: string;
  type: AdminCronParamType;
  defaultValue?: string | boolean;
  placeholder?: string;
  helper?: string;
  required?: boolean;
}

export interface AdminCronVariant {
  id: string;
  label: string;
  endpoint: string;
  method?: "GET" | "POST";
  risk: AdminCronRisk;
  description: string;
  paramKeys?: string[];
  requiredParamKeys?: string[];
}

export interface AdminCronEmailVerificationAction {
  param: AdminCronParam;
  variant: AdminCronVariant;
  emailCount: number;
}

export interface AdminCronJob {
  id: string;
  group: string;
  title: string;
  purpose: string;
  outputFocus: string;
  variants: AdminCronVariant[];
  params?: AdminCronParam[];
  emailVerification?: AdminCronEmailVerificationAction;
  requiredFeatureKey?: string;
  renderer?: "generic" | "holdCleaning";
}

export interface AdminCronRunResult {
  status: "success" | "error";
  endpoint: string;
  variantLabel: string;
  risk: AdminCronRisk;
  durationMs: number;
  finishedAt: string;
  data?: unknown;
  errorMessage?: string;
}

export type AdminCronParamValues = Record<string, string | boolean>;

export const ADMIN_CRON_GROUPS = [
  "Accounts",
  "Forms",
  "Documents",
  "Voyage PnL",
  "Hull cleaning",
  "Operations",
  "Hold Cleaning",
] as const;

export const ADMIN_CRON_JOBS: AdminCronJob[] = [
  {
    id: "hold-cleaning-refresh",
    group: "Hold Cleaning",
    title: "Hold Cleaning refresh",
    purpose: "Runs the complete Hold Cleaning data refresh immediately in an isolated worker process.",
    outputFocus: "Request and run IDs, live stage status, completion outcome, and retryable failures.",
    variants: [],
    requiredFeatureKey: FEATURE_KEYS.HOLD_CLEANING_ADMIN,
    renderer: "holdCleaning",
  },
  {
    id: "create-external-accounts",
    group: "Accounts",
    title: "Create external accounts",
    purpose:
      "Creates missing vessel-side DXO/Okta accounts for onboarding candidates so downstream form and document flows can reach vessel masters.",
    outputFocus: "Candidate vessels, created or skipped accounts, missing vessel-master emails, and DXO/Okta failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-create-external-accounts-dev",
        risk: "dryRun",
        description: "Reads onboarding candidates without creating accounts or writing onboarding rows.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-create-external-accounts-prod",
        risk: "prod",
        description: "Creates real external accounts and records the onboarding outcome in Snowflake.",
      },
    ],
  },
  {
    id: "create-custom-external-account",
    group: "Accounts",
    title: "Create custom external account",
    purpose:
      "Support utility for creating one explicit vessel-side account when the normal candidate query is not enough for an operational recovery.",
    outputFocus: "The requested email address and any account-creation error returned by DXO/Okta.",
    params: [
      {
        key: "debugGmail",
        label: "Email",
        type: "email",
        placeholder: "vessel.master@example.com",
        required: true,
      },
      {
        key: "debugVesselName",
        label: "Vessel name",
        type: "text",
        placeholder: "Vessel name",
        required: true,
      },
    ],
    variants: [
      {
        id: "prod",
        label: "Create account",
        endpoint: "run-create-custom-external-account-prod",
        risk: "prod",
        description: "Creates one real external account for the provided email and vessel name.",
        paramKeys: ["debugGmail", "debugVesselName"],
      },
    ],
  },
  {
    id: "check-external-accounts-active",
    group: "Accounts",
    title: "Check account activation",
    purpose:
      "Polls DXO for provisioned external accounts that have become active and mirrors the activation state back to Opslink reporting.",
    outputFocus: "Accounts checked, newly activated accounts, still-pending accounts, and DXO lookup failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-check-external-accounts-active-dev",
        risk: "dryRun",
        description: "Checks activation candidates without writing activation state.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-check-external-accounts-active-prod",
        risk: "prod",
        description: "Checks real account status and writes successful activation updates.",
      },
    ],
  },
  {
    id: "reinvite-provisioned-accounts",
    group: "Accounts",
    title: "Reinvite provisioned accounts",
    purpose:
      "Resends invitations for provisioned external accounts that still need account activation, helping support recover stale onboarding.",
    outputFocus: "Eligible provisioned accounts, reinvited accounts, skipped accounts, and invite errors.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-reinvite-provisioned-accounts-dev",
        risk: "dryRun",
        description: "Lists accounts that would be reinvited without sending invites.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-reinvite-provisioned-accounts-prod",
        risk: "prod",
        description: "Sends real reinvites to eligible provisioned accounts.",
      },
    ],
  },
  {
    id: "reinvite-one-account",
    group: "Accounts",
    title: "Reinvite one account",
    purpose:
      "Support utility for resending the activation invitation to one explicit account when a vessel master needs targeted help.",
    outputFocus: "The requested account and the DXO/Okta reinvite outcome.",
    params: [
      {
        key: "debugGmail",
        label: "Email",
        type: "email",
        placeholder: "vessel.master@example.com",
        required: true,
      },
    ],
    variants: [
      {
        id: "prod",
        label: "Send reinvite",
        endpoint: "run-reinvite-account-prod",
        risk: "prod",
        description: "Sends a real reinvite to the provided account email.",
        paramKeys: ["debugGmail"],
      },
    ],
  },
  {
    id: "create-tech-forms",
    group: "Forms",
    title: "Create Tech Form requests",
    purpose:
      "Creates missing Tech Form request rows for active fleet vessels so vessel masters can provide technical questionnaire data.",
    outputFocus: "Missing request candidates, created request rows, notification/email settings, and skipped vessels.",
    params: [
      {
        key: "sendNotifs",
        label: "Send notifications",
        type: "checkbox",
        helper: "Prod only. When enabled, the creation run also sends vessel notifications and emails.",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-create-tech-forms-dev",
        risk: "dryRun",
        description: "Finds missing Tech Form requests without inserting rows or sending notifications.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-create-tech-forms-prod",
        risk: "prod",
        description: "Creates real Tech Form requests. Notification sending is controlled by the checkbox.",
        paramKeys: ["sendNotifs"],
      },
    ],
  },
  {
    id: "reset-tech-forms-on-new-time-charter",
    group: "Forms",
    title: "Rejoin Fleet Tech Forms",
    purpose:
      "Creates and sends one fresh Tech Form when a previously onboarded vessel returns under a different time-charter code.",
    outputFocus:
      "Eligible vessel and TCCODE pairs, newly created forms, skipped duplicates, missing emails, offline emails, and Snowflake errors.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-reset-tech-forms-on-new-time-charter-dev",
        risk: "dryRun",
        description: "Lists vessels that would receive a new rejoin Tech Form without creating forms or sending email.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-reset-tech-forms-on-new-time-charter-prod",
        risk: "prod",
        description: "Creates and emails real Tech Forms for eligible vessels rejoining under a new TCCODE.",
      },
    ],
  },
  {
    id: "send-tech-form-reminders",
    group: "Forms",
    title: "Send Tech Form reminders",
    purpose:
      "Sends reminder emails for pending Tech Form requests so vessel masters are chased by cron rather than manual support work.",
    outputFocus:
      "Reminder candidates, recipient grouping, sent emails, duplicate-protected notifications, and send failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-send-tech-form-reminder-emails-dev",
        risk: "dryRun",
        description: "Evaluates reminder candidates without sending vessel emails.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-send-tech-form-reminder-emails-prod",
        risk: "prod",
        description: "Sends real Tech Form reminder emails to eligible vessel masters.",
      },
    ],
  },
  {
    id: "read-tech-form-inbox",
    group: "Forms",
    title: "Read Tech Form inbox",
    // Shows the scheduled offline return import in the standard cron launcher,
    // instead of hiding it in Tech Form debug tools.
    // The dev action only proves the route is present;
    // the prod action is the one that scans the shared return mailbox.
    purpose:
      "Reads the shared offline Tech Form return mailbox and imports returned JSON attachments that match a Tech Form offline id.",
    outputFocus:
      "Mailbox health, candidate attachments, processed returns, duplicate skips, failed imports, and stored procedure results.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-read-tech-form-inbox-dev",
        risk: "dryRun",
        description: "Reports the configured mailbox job without importing attachments.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-read-tech-form-inbox-prod",
        risk: "prod",
        description: "Scans the shared return mailbox and saves matching offline Tech Form returns.",
      },
    ],
  },
  {
    id: "process-crew-report-onboarding",
    group: "Forms",
    title: "Process Crew Reports",
    purpose:
      "Imports Crew Report mailbox replies, sends authenticated validation guidance to Masters, and processes Period-vessel onboarding and long-tenure refresh requests, chasers, and Ops Support escalations.",
    outputFocus:
      "Mailbox health, imported responses, Master correction email counts and errors, onboarding and long-tenure campaigns, separate escalations, and delivery outcomes.",
    emailVerification: {
      param: {
        key: "debugEmail",
        label: "Test email recipient",
        type: "email",
        placeholder: "Insert debug email",
        required: true,
      },
      variant: {
        id: "verify-emails",
        label: "Send",
        endpoint: "run-verify-crew-report-onboarding-emails",
        method: "POST",
        risk: "verification",
        description:
          "Sends six synthetic Crew Report verification emails, including a Master correction example, only to the provided Cargill debug recipient.",
        paramKeys: ["debugEmail"],
        requiredParamKeys: ["debugEmail"],
      },
      emailCount: 6,
    },
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-process-crew-report-onboarding-dev",
        risk: "dryRun",
        description:
          "Checks mailbox health and previews onboarding plus long-tenure decisions without importing workbooks, changing Crew Report state, assigning request series, or sending emails.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-process-crew-report-onboarding-prod",
        risk: "prod",
        description:
          "Runs the live mailbox import, sends authenticated Master correction guidance, and executes onboarding plus long-tenure requests, chasers, resolutions, and Ops Support escalations.",
      },
    ],
  },
  {
    id: "create-baltic-forms",
    group: "Forms",
    title: "Create Baltic Questionnaire requests",
    purpose:
      "Creates missing Baltic Questionnaire request rows and can import Q88 Baltic 99 data as an initial vessel questionnaire baseline.",
    outputFocus:
      "Missing request candidates, created rows, Q88 import outcomes, notification/email settings, and skipped vessels.",
    params: [
      {
        key: "sendNotifs",
        label: "Send notifications",
        type: "checkbox",
        helper: "Prod only. When enabled, the creation run also sends vessel notifications and emails.",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-create-baltic-forms-dev",
        risk: "dryRun",
        description: "Finds missing Baltic Questionnaire requests without inserting rows or sending notifications.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-create-baltic-forms-prod",
        risk: "prod",
        description: "Creates real Baltic Questionnaire requests. Notification sending is controlled by the checkbox.",
        paramKeys: ["sendNotifs"],
      },
    ],
  },
  {
    id: "send-baltic-form-reminders",
    group: "Forms",
    title: "Send Baltic Questionnaire reminders",
    purpose:
      "Sends reminder emails for pending Baltic Questionnaire requests using the same cron-owned reminder boundary as Tech Forms.",
    outputFocus:
      "Reminder candidates, recipient grouping, sent emails, duplicate-protected notifications, and send failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-send-baltic-form-reminder-emails-dev",
        risk: "dryRun",
        description: "Evaluates Baltic reminder candidates without sending vessel emails.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-send-baltic-form-reminder-emails-prod",
        risk: "prod",
        description: "Sends real Baltic Questionnaire reminder emails to eligible vessel masters.",
      },
    ],
  },
  {
    id: "import-baltic-questionnaire",
    group: "Forms",
    title: "Import Baltic Questionnaire",
    purpose:
      "Runs the Q88 Baltic Questionnaire import path manually for a single IMO or for the current import candidate set.",
    outputFocus:
      "Imported sections, skipped IMO numbers, Q88 availability, stored-procedure updates, and import errors.",
    params: [
      {
        key: "imoNumber",
        label: "IMO number",
        type: "text",
        placeholder: "Optional single IMO",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-import-baltic-questionnaire-dev",
        risk: "dryRun",
        description: "Runs import discovery without applying stored-procedure writes.",
        paramKeys: ["imoNumber"],
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-import-baltic-questionnaire-prod",
        risk: "prod",
        description: "Imports real Baltic Questionnaire data through the production write path.",
        paramKeys: ["imoNumber"],
      },
    ],
  },
  {
    id: "create-onboarding-certificate-requests",
    group: "Documents",
    title: "Create onboarding certificate requests",
    purpose:
      "Starts the onboarding certificate request process for vessels that newly entered Opslink after the configured go-live date or have a current vessel name different from an earlier automated request.",
    outputFocus:
      "Onboarding candidates, name-change candidates, vessels that would receive document requests, missing emails, created request rows, sent emails, and verification sends.",
    params: [
      {
        key: "goLiveDate",
        label: "Go-live date",
        type: "text",
        placeholder: "YYYY-MM-DD",
        helper: "In prod this can force certificate request emails before the rollout date: 2026-05-19",
      },
      {
        key: "debugEmail",
        label: "Debug email",
        type: "email",
        placeholder: "name@cargill.com",
        helper: "Required only for Send test email.",
      },
      {
        key: "imoNumber",
        label: "Selected IMO number",
        type: "text",
        placeholder: "Run dry run and select a candidate",
        helper: "Required only for Send test email. You can also paste an IMO manually.",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-create-onboarding-certificate-requests-dev",
        risk: "dryRun",
        description:
          "Lists eligible onboarding and name-change certificate request candidates without writing rows or sending emails.",
        paramKeys: ["goLiveDate"],
      },
      {
        id: "verify-dev",
        label: "Send test email",
        endpoint: "run-verify-onboarding-certificate-request-email-dev",
        risk: "verification",
        description:
          "Sends one certificate request email to the debug email for the selected IMO. Does not create DOCUMENTS rows or email the vessel master.",
        paramKeys: ["debugEmail", "imoNumber", "goLiveDate"],
        requiredParamKeys: ["debugEmail", "imoNumber"],
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-create-onboarding-certificate-requests-prod",
        risk: "prod",
        description: "Creates real DOCUMENTS request rows and sends real certificate-entry emails to vessel masters.",
        paramKeys: ["goLiveDate"],
      },
    ],
  },
  {
    id: "send-document-request-reminders",
    group: "Documents",
    title: "Send missing-document reminders",
    purpose:
      "Sends grouped reminder emails for pending missing-document requests so vessel masters receive consolidated follow-up.",
    outputFocus: "Pending document requests, grouped vessel-master recipients, sent emails, and send failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-send-doc-request-reminder-emails-dev",
        risk: "dryRun",
        description: "Evaluates missing-document reminder candidates without sending emails.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-send-doc-request-reminder-emails-prod",
        risk: "prod",
        description: "Sends real missing-document reminder emails.",
      },
    ],
  },
  {
    id: "send-document-renewal-reminders",
    group: "Documents",
    title: "Send document-renewal reminders",
    purpose:
      "Sends weekly renewal reminders for active onboarded vessels and creates Baltic renewal requests when that document is due.",
    outputFocus:
      "Renewal candidates, grouped recipients, created Baltic renewal requests, sent emails, and send failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-send-doc-renewal-reminder-emails-dev",
        risk: "dryRun",
        description: "Evaluates renewal reminder candidates without sending emails or creating renewal requests.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-send-doc-renewal-reminder-emails-prod",
        risk: "prod",
        description: "Sends real renewal reminder emails and performs eligible renewal-side effects.",
      },
    ],
  },
  {
    id: "store-certificates-kpis-daily-snapshot",
    group: "Documents",
    title: "Store certificate KPI daily snapshot",
    purpose:
      "Stores the current Period Fleet Certificates and Period Fleet Baltic Questionnaire KPI metrics in the daily Snowflake snapshot table.",
    outputFocus:
      "Snapshot date, period fleet vessel total, computed certificate and Baltic Questionnaire metrics, and Snowflake merge result.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-certificates-kpis-daily-snapshot-dev",
        risk: "dryRun",
        description: "Computes the KPI snapshot and writes a cron log without updating the daily snapshot table.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-certificates-kpis-daily-snapshot-prod",
        risk: "prod",
        description: "Writes or updates today's KPI snapshot row in Snowflake.",
      },
    ],
  },
  {
    id: "create-vessel-assessment-notifications",
    group: "Voyage PnL",
    title: "Create vessel-assessment notifications",
    purpose:
      "Creates board notifications for qualifying completed voyages that still need vessel-owner assessment work.",
    outputFocus:
      "Qualifying voyages, created notifications, duplicates, target filters, and notification write failures.",
    params: [
      {
        key: "tcCode",
        label: "TC code",
        type: "text",
        placeholder: "Optional target TC",
      },
      {
        key: "voyageNo",
        label: "Voyage no.",
        type: "text",
        placeholder: "Optional target voyage",
      },
      {
        key: "vesselCode",
        label: "Vessel code",
        type: "text",
        placeholder: "Optional vessel code",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-create-vessel-assesments-notifications-dev",
        risk: "dryRun",
        description: "Evaluates notification candidates with run=false and does not send verification emails.",
        paramKeys: ["tcCode", "voyageNo", "vesselCode"],
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-create-vessel-assesments-notifications-prod",
        risk: "prod",
        description: "Creates real vessel-assessment notifications. Email verification uses the dedicated route.",
        paramKeys: ["tcCode", "voyageNo", "vesselCode"],
      },
    ],
  },
  {
    id: "verify-voyage-pnl-email",
    group: "Voyage PnL",
    title: "Verify Voyage PnL email",
    purpose:
      "Sends one manual Voyage PnL verification email to a provided debug recipient instead of using the group-mail route.",
    outputFocus:
      "Target voyage, debug recipient, force-send setting, rendered email decision, and email-service errors.",
    params: [
      {
        key: "debugEmail",
        label: "Debug email",
        type: "email",
        placeholder: "name@cargill.com",
        required: true,
      },
      {
        key: "tcCode",
        label: "TC code",
        type: "text",
        placeholder: "Required",
        required: true,
      },
      {
        key: "voyageNo",
        label: "Voyage no.",
        type: "text",
        placeholder: "Required",
        required: true,
      },
      {
        key: "vesselCode",
        label: "Vessel code",
        type: "text",
        placeholder: "Optional",
      },
      {
        key: "forceVerificationSend",
        label: "Force send",
        type: "checkbox",
        helper: "Sends even when normal verification rules would skip the email.",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Send dev check",
        endpoint: "run-verify-voyage-pnl-email-dev",
        risk: "verification",
        description: "Sends the verification email only to the provided debug recipient.",
        paramKeys: ["debugEmail", "tcCode", "voyageNo", "vesselCode", "forceVerificationSend"],
      },
      {
        id: "prod",
        label: "Send prod check",
        endpoint: "run-verify-voyage-pnl-email-prod",
        risk: "verification",
        description: "Uses prod data but still sends only to the provided debug recipient.",
        paramKeys: ["debugEmail", "tcCode", "voyageNo", "vesselCode", "forceVerificationSend"],
      },
    ],
  },
  {
    id: "expire-vessel-assessment-notifications",
    group: "Voyage PnL",
    title: "Expire vessel-assessment notifications",
    purpose:
      "Removes stale vessel-assessment notifications after the retention window so users do not keep obsolete PnL tasks.",
    outputFocus: "Expired notifications, retained notifications, stale candidates, and delete failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-expire-vessel-assesments-notifications-dev",
        risk: "dryRun",
        description: "Finds stale assessment notifications without deleting rows.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-expire-vessel-assesments-notifications-prod",
        risk: "prod",
        description: "Deletes stale vessel-assessment notifications.",
      },
    ],
  },
  {
    id: "load-voyage-pnl-summary",
    group: "Voyage PnL",
    title: "Load Voyage PnL summary",
    purpose:
      "Refreshes the Voyage PnL summary read model used by assessment and review flows, either for recent days or one explicit voyage.",
    outputFocus: "Loaded voyages, skipped voyages, target filters, Snowflake write counts, and load errors.",
    params: [
      {
        key: "days",
        label: "Days",
        type: "number",
        placeholder: "Optional recent-day window",
      },
      {
        key: "tcCode",
        label: "TC code",
        type: "text",
        placeholder: "Use with voyage no.",
      },
      {
        key: "voyageNo",
        label: "Voyage no.",
        type: "number",
        placeholder: "Use with TC code",
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-load-voyage-pnl-summary-dev",
        risk: "dryRun",
        description: "Loads and reports candidate data without writing the summary model.",
        paramKeys: ["days", "tcCode", "voyageNo"],
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-load-voyage-pnl-summary-prod",
        risk: "prod",
        description: "Writes refreshed Voyage PnL summary data.",
        paramKeys: ["days", "tcCode", "voyageNo"],
      },
    ],
  },
  {
    id: "hull-cleaning-request-created",
    group: "Hull cleaning",
    title: "Process hull-cleaning request-created emails",
    purpose:
      "Processes the hull-cleaning request-created email outbox and creates board notifications for eligible recipients.",
    outputFocus: "Outbox rows, sent request-created emails, notification writes, skipped rows, and send failures.",
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-hull-cleaning-request-created-dev",
        risk: "dryRun",
        description: "Evaluates eligible outbox rows through the non-prod run path.",
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-hull-cleaning-request-created-prod",
        risk: "prod",
        description: "Sends real hull-cleaning request-created emails and creates recipient notifications.",
      },
    ],
  },
  {
    id: "send-ops-daily-emails",
    group: "Operations",
    title: "Send Ops daily emails",
    purpose:
      "Sends the daily laycan, first redelivery-notice, and hold-cleaning campaigns, followed by the operations morning summary.",
    outputFocus:
      "Campaign candidates, sent or skipped emails, duplicate protection, summary delivery, and send failures.",
    params: [
      {
        key: "debugEmail",
        label: "Debug email",
        type: "email",
        placeholder: "name@cargill.com",
        helper: "Verification runs send every campaign email and the summary only to this address.",
        required: true,
      },
    ],
    variants: [
      {
        id: "dev",
        label: "Dry run",
        endpoint: "run-ops-daily-emails-dev",
        risk: "dryRun",
        description: "Evaluates today's campaign candidates without sending campaign or summary emails.",
      },
      {
        id: "verify-emails",
        label: "Send prod check",
        endpoint: "run-verify-ops-daily-emails-prod",
        risk: "verification",
        description:
          "Uses today's prod data and the real templates, but sends every email only to the provided debug recipient. Vessels, desks and coordinators are not contacted.",
        paramKeys: ["debugEmail"],
        requiredParamKeys: ["debugEmail"],
      },
      {
        id: "prod",
        label: "Run prod",
        endpoint: "run-ops-daily-emails-prod",
        risk: "prod",
        description: "Sends real campaign emails and the operations morning summary.",
      },
    ],
  },
];
